import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, ShieldCheck, ArrowLeft, Loader2, CheckCircle2 } from 'lucide-react';
import { authApi } from '@verlo/shared/services/api';
import { Button } from '@verlo/shared/components/ui/button';
import { Input } from '@verlo/shared/components/ui/input';
import { Label } from '@verlo/shared/components/ui/label';
import CodeInput from '../components/auth/CodeInput';

const MIN_PASSWORD_LENGTH = 10;

/**
 * A forgotten password (#179). Cognito emails a six-digit code, which comes back here with
 * the new password; then they sign in as normal — with their authenticator too, if they have
 * one, since a reset changes the password and nothing else. Replaces the owner/GM magic link,
 * which signed its holder in with neither.
 *
 * The page never says whether an address has an account: the second step reads the same
 * either way. Same shell as LoginPage — bg-surface-2, Verlo mark up top, one card.
 */
const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [stage, setStage] = useState('email'); // email → code → done
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const requestCode = async (e) => {
    e?.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    setError('');
    try {
      await authApi.passwordForgot(email.trim().toLowerCase());
      setCode('');
      setStage('code');
    } catch (err) {
      // A 429 or an outage. Anything about the address itself never reaches here.
      setError(err.response?.data?.detail || 'That didn\'t go through. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const reset = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Use at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (password !== confirm) {
      setError("The two passwords don't match.");
      return;
    }
    setSubmitting(true);
    try {
      await authApi.passwordReset({ email: email.trim().toLowerCase(), code, password });
      setStage('done');
    } catch (err) {
      setError(err.response?.data?.detail || 'That didn\'t go through. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-surface-2 flex flex-col items-center justify-center px-6 py-10"
      data-testid="forgot-password-page"
    >
      <div className="w-full max-w-xs mb-8 shrink-0">
        <img src="/verlo-logo-blue.svg" alt="Verlo" className="w-full h-auto max-h-16 object-contain" />
      </div>

      <div className="w-full max-w-sm">
        <div className="bg-white border border-hairline rounded-lg  p-6">
          {stage !== 'done' && (
            <Link
              to="/login"
              className="inline-flex items-center gap-1 text-[12px] text-ink-faint hover:text-ink-soft mb-4"
              data-testid="forgot-back-login"
            >
              <ArrowLeft size={13} />
              Back to sign in
            </Link>
          )}

          {stage === 'email' && (
            <>
              <h1 className="text-lg font-semibold text-ink">Reset your password</h1>
              <p className="text-[13px] text-ink-soft mt-1.5 mb-5">
                We&apos;ll email you a code to choose a new one.
              </p>
              <form onSubmit={requestCode} className="space-y-3">
                <div className="space-y-1.5">
                  <Label htmlFor="forgot-email" className="text-xs text-ink-soft">Email address</Label>
                  <Input
                    id="forgot-email"
                    type="email"
                    autoComplete="email"
                    required
                    autoFocus
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@restaurant.com"
                    data-testid="forgot-email"
                  />
                </div>
                {error && <p className="text-[13px] text-signal-stop" data-testid="forgot-error">{error}</p>}
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-verlo hover:bg-verlo-hover gap-1.5"
                  data-testid="forgot-send"
                >
                  {submitting ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
                  {submitting ? 'Sending…' : 'Email me a code'}
                </Button>
              </form>
            </>
          )}

          {stage === 'code' && (
            <>
              <h1 className="text-lg font-semibold text-ink">Check your email</h1>
              <p className="text-[13px] text-ink-soft mt-1.5 mb-5">
                If <span className="text-ink font-semibold">{email}</span> has an account, a code is
                on its way. It comes from{' '}
                <span className="whitespace-nowrap">no-reply@verificationemail.com</span> and can take a minute. Check
                your spam folder if it doesn&apos;t arrive.
              </p>
              <form onSubmit={reset} className="space-y-3">
                <div className="space-y-1.5">
                  <Label htmlFor="forgot-code" className="text-xs text-ink-soft">Code</Label>
                  <CodeInput id="forgot-code" value={code} onChange={setCode} data-testid="forgot-code" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="forgot-password" className="text-xs text-ink-soft">New password</Label>
                  <Input
                    id="forgot-password"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="forgot-password"
                  />
                  <p className="text-[12px] text-ink-faint">At least {MIN_PASSWORD_LENGTH} characters.</p>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="forgot-confirm" className="text-xs text-ink-soft">Confirm</Label>
                  <Input
                    id="forgot-confirm"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    data-testid="forgot-confirm"
                  />
                </div>
                {error && <p className="text-[13px] text-signal-stop" data-testid="forgot-error">{error}</p>}
                <Button
                  type="submit"
                  disabled={submitting || code.length !== 6}
                  className="w-full bg-verlo hover:bg-verlo-hover"
                  data-testid="forgot-reset"
                >
                  {submitting ? 'Saving…' : 'Set new password'}
                </Button>
              </form>
              <div className="mt-4 text-center">
                <button
                  type="button"
                  onClick={requestCode}
                  disabled={submitting}
                  className="text-[12px] text-ink-soft hover:text-verlo hover:underline"
                  data-testid="forgot-resend"
                >
                  Send a new code
                </button>
              </div>
            </>
          )}

          {stage === 'done' && (
            <div className="text-center" data-testid="forgot-done">
              <div className="w-14 h-14 mx-auto rounded-full bg-signal-ok-tint border border-signal-ok-line flex items-center justify-center">
                <CheckCircle2 size={26} className="text-signal-ok" />
              </div>
              <h1 className="text-lg font-semibold text-ink mt-4">Password changed</h1>
              <p className="text-[13px] text-ink-soft mt-2">
                You&apos;ve been signed out everywhere else. Sign in with your new password.
              </p>
              <Button
                className="mt-6 w-full bg-verlo hover:bg-verlo-hover"
                onClick={() => navigate('/login')}
                data-testid="forgot-sign-in"
              >
                Sign in
              </Button>
            </div>
          )}

          {stage === 'email' && (
            <p className="text-[11px] text-ink-faint mt-5 flex items-start gap-1.5">
              <ShieldCheck size={11} className="mt-0.5 shrink-0" />
              We don&apos;t say whether an email has an account. If yours does, the code arrives by email.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
